/**
 * Staff-initiated proposal signing when the client approves verbally (e.g. on a call).
 * Writes a contract signature, commercial snapshot, and signed PDF — same artifacts as client portal sign.
 */
import React from 'react';
import { pdf } from '@react-pdf/renderer';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { getPublicSettings } from './appSettings';
import { resolveEnhancedDealIdForProposalDoc } from './proposalCanonicalDealId';
import {
  buildDefaultStructureSelections,
  computeProposalGrandTotals,
  getClientVisibleNodeSets,
  signedIncludedStructureLayoutKey,
  getEffectiveIncludeInstallForContractDisplay,
  parseSpeciesSelectionsForPublicTotals,
  parseStructureSelectionsForPublicTotals,
} from './proposalPublicGrandTotals';
import { commitOfferedPhases, isPhasedProposal } from './proposalPhases';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import ProposalPdfDocument from '../components/proposals/ProposalPdfDocument';
import type { ProposalBuilderData } from '../types';

export const CLIENT_DIRECTION_SIGNATURE_LABEL = "Signed at Client's Direction";

/** Set only when staff advances portal to Quote Signed without a client signature. */
export const PORTAL_INTERNAL_SIGNATURE_SOURCE = 'portal-internal-advance';

export type ProposalSignatureLike =
  | { dataUrl?: string; signedBy?: string; source?: string }
  | null
  | undefined;

export function isInternalClientDirectionSignature(
  signature: ProposalSignatureLike,
): boolean {
  if (!signature?.dataUrl) return false;
  return (
    signature.source === PORTAL_INTERNAL_SIGNATURE_SOURCE ||
    signature.signedBy === CLIENT_DIRECTION_SIGNATURE_LABEL
  );
}

/** True when the client signed on the public proposal (not staff portal advance). */
export function hasClientPortalSignature(signature: ProposalSignatureLike): boolean {
  if (!signature?.dataUrl) return false;
  return !isInternalClientDirectionSignature(signature);
}

/** Canvas signature image for staff-initiated / verbal client approval. */
export function clientDirectionSignatureDataUrl(
  label: string = CLIENT_DIRECTION_SIGNATURE_LABEL,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = 440;
  canvas.height = 88;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#0f172a';
  ctx.font = 'italic 20px Georgia, "Times New Roman", serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

async function loadProposalPdfLogoBase64(): Promise<string | undefined> {
  try {
    const settings = await getPublicSettings();
    if (settings.engagementLogo?.url) {
      const logoUrl = settings.engagementLogo.url.includes('?')
        ? `${settings.engagementLogo.url}&t=${Date.now()}`
        : `${settings.engagementLogo.url}?t=${Date.now()}`;
      const response = await fetch(logoUrl, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
      if (response.ok) {
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      }
    }
  } catch {
    /* optional */
  }
  try {
    const fallbackResponse = await fetch('/assets/logo/LC_pdf_logo.png');
    if (fallbackResponse.ok) {
      const fallbackBlob = await fallbackResponse.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(fallbackBlob);
      });
    }
  } catch {
    /* optional */
  }
  return undefined;
}

function buildSpeciesSelectionsSnapshot(
  proposal: ProposalBuilderData,
  structureSelections: Record<string, boolean>,
): Record<string, string> {
  const { selectedSpecies, kitSelectedSpecies } = parseSpeciesSelectionsForPublicTotals(proposal);
  const visible = getClientVisibleNodeSets(proposal, structureSelections);
  const out: Record<string, string> = {};
  visible.forEach(ns => {
    const raw =
      selectedSpecies[ns.id] ||
      (proposal.isKitProposal && proposal.nodeSets.length === 1 ? kitSelectedSpecies : null) ||
      proposal.activeSpecies ||
      'default';
    out[ns.id] =
      raw === 'default'
        ? ns.materials.timberList[0]?.species || 'Default'
        : raw;
  });
  return out;
}

async function generateAndUploadSignedPdf(
  proposal: ProposalBuilderData,
  proposalDocId: string,
  signature: { dataUrl: string; signedBy: string; signedAt: Date },
  options: {
    includeInstall: boolean;
    speciesSelections: Record<string, string>;
    structureSelections: Record<string, boolean>;
    kitSelectedSpecies?: string | null;
  },
): Promise<string> {
  const logoBase64 = await loadProposalPdfLogoBase64();
  const pdfDoc = (
    <ProposalPdfDocument
      proposal={proposal}
      companyLogo={logoBase64}
      includeInstall={options.includeInstall}
      selectedSpecies={options.speciesSelections}
      kitSelectedSpecies={options.kitSelectedSpecies ?? undefined}
      structureSelections={options.structureSelections}
      generatedDate={ new Date() }
      signature={signature}
      showOnlySelected={true}
    />
  );
  const pdfBlob = await pdf(pdfDoc).toBlob();
  const pdfFileName = `signed-proposals/${proposalDocId}/${proposal.slug || proposalDocId}-signed.pdf`;
  const storageRef = ref(storage, pdfFileName);
  await uploadBytes(storageRef, pdfBlob);
  return getDownloadURL(storageRef);
}

export type ApplyInternalClientDirectionSignResult =
  | { ok: true; signedPdfUrl: string; alreadySigned?: boolean }
  | { ok: false; error: string };

/**
 * Apply verbal / phone approval: signature block, commercial snapshot, signed PDF.
 * Idempotent when signature + PDF already exist.
 */
export async function applyInternalClientDirectionSign(
  proposalDocId: string,
): Promise<ApplyInternalClientDirectionSignResult> {
  try {
    const proposalRef = doc(db, 'proposalBuilders', proposalDocId);
    const snap = await getDoc(proposalRef);
    if (!snap.exists()) {
      return { ok: false, error: 'Proposal not found' };
    }

    let proposal = { id: snap.id, ...snap.data() } as ProposalBuilderData;
    const now = new Date();
    const signedAtTs = Timestamp.now();

    const structureSelections =
      parseStructureSelectionsForPublicTotals(proposal) ??
      buildDefaultStructureSelections(proposal);
    const { kitSelectedSpecies } = parseSpeciesSelectionsForPublicTotals(proposal);
    const shouldIncludeInstall = getEffectiveIncludeInstallForContractDisplay(proposal, true);
    const speciesSelections = buildSpeciesSelectionsSnapshot(proposal, structureSelections);

    const selectedSpeciesForTotals: Record<string, string> = {};
    Object.entries(speciesSelections).forEach(([nsId, name]) => {
      const ns = proposal.nodeSets.find(n => n.id === nsId);
      const defaultName = ns?.materials.timberList[0]?.species;
      selectedSpeciesForTotals[nsId] =
        defaultName && name === defaultName ? 'default' : name;
    });

    const grandTotals = computeProposalGrandTotals(proposal, {
      includeInstall: shouldIncludeInstall,
      selectedSpecies: selectedSpeciesForTotals,
      kitSelectedSpecies,
      structureSelections,
    });

    let signature = proposal.signature as
      | { dataUrl?: string; signedBy?: string; signedAt?: unknown; source?: string }
      | undefined;

    if (!signature?.dataUrl) {
      const dataUrl = clientDirectionSignatureDataUrl();
      if (!dataUrl) {
        return { ok: false, error: 'Could not render signature image' };
      }
      signature = {
        dataUrl,
        signedBy: CLIENT_DIRECTION_SIGNATURE_LABEL,
        signedAt: signedAtTs,
        source: PORTAL_INTERNAL_SIGNATURE_SOURCE,
      };

      const crmDealId = await resolveEnhancedDealIdForProposalDoc(db, proposalDocId, proposal.dealId);

      const proposalSignUpdate = removeUndefinedDeep({
        signature,
        status: 'approved',
        approvedAt: signedAtTs,
        updatedAt: signedAtTs,
        slugLocked: true,
        ...(crmDealId ? { dealId: crmDealId } : {}),
        grandTotal: grandTotals.total,
        selectedSpecies: speciesSelections,
        installSelection: shouldIncludeInstall,
        structureSelections,
        speciesPricing: {
          selections: speciesSelections,
          designTotal: grandTotals.design,
          timberTotal: grandTotals.timberKit,
          installTotal: shouldIncludeInstall ? grandTotals.install : 0,
          billableTotal: grandTotals.total,
          discountAmount: grandTotals.discountAmount,
          structureSelections,
        },
        ...(isPhasedProposal(proposal)
          ? {
              phases: commitOfferedPhases(proposal.phases || [], {
                committedBy: CLIENT_DIRECTION_SIGNATURE_LABEL,
                signature: {
                  dataUrl,
                  signedBy: CLIENT_DIRECTION_SIGNATURE_LABEL,
                  signedAt: now,
                },
              }),
            }
          : {}),
      });

      await updateDoc(proposalRef, proposalSignUpdate);
      proposal = { ...proposal, ...proposalSignUpdate, signature } as ProposalBuilderData;
    }

    if (proposal.signedPdfUrl) {
      return { ok: true, signedPdfUrl: proposal.signedPdfUrl, alreadySigned: true };
    }

    const sigForPdf = {
      dataUrl: String(signature!.dataUrl),
      signedBy: String(
        signature!.signedBy ||
          (isInternalClientDirectionSignature(signature)
            ? CLIENT_DIRECTION_SIGNATURE_LABEL
            : 'Customer'),
      ),
      signedAt:
        (signature!.signedAt as { toDate?: () => Date })?.toDate?.() ??
        (signature!.signedAt instanceof Date ? signature!.signedAt : now),
    };

    const signedPdfUrl = await generateAndUploadSignedPdf(proposal, proposalDocId, sigForPdf, {
      includeInstall: shouldIncludeInstall,
      speciesSelections,
      structureSelections,
      kitSelectedSpecies,
    });

    const signedPdfLayoutKey = signedIncludedStructureLayoutKey(structureSelections);
    await updateDoc(proposalRef, {
      signedPdfUrl,
      ...(signedPdfLayoutKey ? { signedPdfLayoutKey } : {}),
      updatedAt: Timestamp.now(),
    });

    return { ok: true, signedPdfUrl };
  } catch (err) {
    console.error('[applyInternalClientDirectionSign]', err);
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Internal sign failed',
    };
  }
}
